import React from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"

import { ScheduleStatus, WeeklySchedule } from "./raffleSchedule"

// Presentational only: no Firebase/router/app imports, everything arrives through props, so this
// file can move into the shared react-ui-components library unchanged (see
// docs/raffle-weekly-schedule-2026-10-21.md for why it still lives here for now).

export const WEEKDAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
]

export const DEFAULT_WEEKLY_SCHEDULE_LABELS = {
  title: "Programación automática / Weekly Schedule",
  description:
    "Repeats this raffle every week on the selected days. While enabled, the schedule controls Raffle Date and Visible From: on each run Visible From is the publish time and Raffle Date is that run's end time. Participations, winners and results are never reset. Turn it off to go back to a one-time raffle using Raffle Date / Visible From.",
  enable: "Enable weekly schedule",
  oneTime: "One-time raffle (Raffle Date / Visible From)",
  recurring: "Recurring raffle (weekly schedule)",
  weekdays: "Days of the week",
  publishTime: "Publish time",
  endTime: "End / hide time",
  endsNextDay: "Ends the next day",
  timezone: "Time zone",
  startDate: "Schedule start date",
  repeatIndefinitely: "Repeat indefinitely",
  endDate: "Schedule end date",
  pause: "Pause",
  resume: "Resume",
  cancel: "Cancel schedule",
  nextPublish: "Next publish",
  nextEnd: "Next hide",
  none: "None",
  status: "Status",
  statusLabels: {
    off: "Not scheduled",
    invalid: "Incomplete",
    paused: "Paused",
    active: "Active",
    finished: "Finished",
  } as Record<ScheduleStatus, string>,
  disabledNotice: "This raffle is disabled, so the schedule will not run until it is re-enabled.",
}

export type WeeklyScheduleLabels = typeof DEFAULT_WEEKLY_SCHEDULE_LABELS

export interface WeeklyScheduleViewProps {
  value: WeeklySchedule
  status: ScheduleStatus
  // Pre-formatted by the container (already in the schedule's time zone).
  nextPublishLabel?: string | null
  nextEndLabel?: string | null
  timeZones: string[]
  errors?: string[]
  raffleDisabled?: boolean
  disabled?: boolean
  readOnly?: boolean
  labels?: Partial<WeeklyScheduleLabels>
  onChange: (patch: Partial<WeeklySchedule>) => void
  onCancel: () => void
}

const STATUS_COLOR: Record<ScheduleStatus, "default" | "success" | "warning" | "error" | "info"> = {
  off: "default",
  invalid: "error",
  paused: "warning",
  active: "success",
  finished: "info",
}

const WeeklyScheduleViewComponent = ({
  value,
  status,
  nextPublishLabel,
  nextEndLabel,
  timeZones,
  errors = [],
  raffleDisabled,
  disabled,
  readOnly,
  labels: labelOverrides,
  onChange,
  onCancel,
}: WeeklyScheduleViewProps) => {
  const labels = React.useMemo(
    () => ({ ...DEFAULT_WEEKLY_SCHEDULE_LABELS, ...labelOverrides }),
    [labelOverrides],
  )
  const enabled = value.enabled === true
  const locked = Boolean(disabled || readOnly)
  const weekdays = value.weekdays || []
  const endsNextDay =
    Boolean(value.publishTime) && Boolean(value.endTime) && value.endTime! <= value.publishTime!

  return (
    <Box
      data-testid="weekly-schedule"
      sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: 2 }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {labels.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {labels.description}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                disabled={locked}
                inputProps={{ "aria-label": labels.enable }}
                onChange={(event) => onChange({ enabled: event.target.checked, paused: false })}
              />
            }
            label={labels.enable}
          />
          <Chip
            size="small"
            variant="outlined"
            color={enabled ? "primary" : "default"}
            label={enabled ? labels.recurring : labels.oneTime}
          />
          <Chip
            size="small"
            color={STATUS_COLOR[status]}
            label={`${labels.status}: ${labels.statusLabels[status]}`}
            data-testid="weekly-schedule-status"
          />
        </Stack>

        {enabled && (
          <>
            {raffleDisabled && <Alert severity="warning">{labels.disabledNotice}</Alert>}

            <Box>
              <Typography variant="caption" color="text.secondary">
                {labels.weekdays}
              </Typography>
              <ToggleButtonGroup
                size="small"
                color="primary"
                value={weekdays}
                disabled={locked}
                aria-label={labels.weekdays}
                onChange={(_event, next: number[]) =>
                  onChange({ weekdays: [...next].sort((a, b) => a - b) })
                }
                sx={{ display: "flex", flexWrap: "wrap" }}
              >
                {WEEKDAY_OPTIONS.map((day) => (
                  <ToggleButton key={day.value} value={day.value} aria-label={day.label}>
                    {day.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={labels.publishTime}
                type="time"
                size="small"
                value={value.publishTime || ""}
                disabled={locked}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => onChange({ publishTime: event.target.value })}
              />
              <TextField
                label={labels.endTime}
                type="time"
                size="small"
                value={value.endTime || ""}
                disabled={locked}
                InputLabelProps={{ shrink: true }}
                helperText={endsNextDay ? labels.endsNextDay : undefined}
                onChange={(event) => onChange({ endTime: event.target.value })}
              />
              <TextField
                select
                label={labels.timezone}
                size="small"
                value={value.timezone || ""}
                disabled={locked}
                sx={{ minWidth: 220 }}
                onChange={(event) => onChange({ timezone: event.target.value })}
              >
                {timeZones.map((zone) => (
                  <MenuItem key={zone} value={zone}>
                    {zone}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
              <TextField
                label={labels.startDate}
                type="date"
                size="small"
                value={value.startDate || ""}
                disabled={locked}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => onChange({ startDate: event.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={value.repeatIndefinitely === true}
                    disabled={locked}
                    onChange={(event) => onChange({ repeatIndefinitely: event.target.checked })}
                  />
                }
                label={labels.repeatIndefinitely}
              />
              {value.repeatIndefinitely !== true && (
                <TextField
                  label={labels.endDate}
                  type="date"
                  size="small"
                  value={value.endDate || ""}
                  disabled={locked}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => onChange({ endDate: event.target.value })}
                />
              )}
            </Stack>

            <Stack spacing={0.5} data-testid="weekly-schedule-next">
              <Typography variant="body2">
                <strong>{labels.nextPublish}:</strong> {nextPublishLabel || labels.none}
              </Typography>
              <Typography variant="body2">
                <strong>{labels.nextEnd}:</strong> {nextEndLabel || labels.none}
              </Typography>
            </Stack>

            {errors.map((message) => (
              <Alert key={message} severity="error">
                {message}
              </Alert>
            ))}

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={locked}
                onClick={() => onChange({ paused: value.paused !== true })}
              >
                {value.paused === true ? labels.resume : labels.pause}
              </Button>
              <Button size="small" color="error" disabled={locked} onClick={onCancel}>
                {labels.cancel}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  )
}

export const WeeklyScheduleView = React.memo(WeeklyScheduleViewComponent)
export default WeeklyScheduleView
